import React, { useState, useEffect } from "react";
import UpdateModal from "./UpdateModal";
import { updateUserAddress, fetchUserProfile } from "../../services/user.service";

export default function AddressInfo({ addressInfo, locationOptions }) {
  const DEFAULT_ADDRESS = {
    title: "Address",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    companyEmail: ""
  };
  const DESCRIPTION =
    "You can manage the company’s physical or operational address information here. It is used in processes such as shipping and billing addresses.";

  const initialAddress = addressInfo?.addressCard || DEFAULT_ADDRESS;
  const [addressCard, setAddressCard] = useState(initialAddress);

  // Keep local state in sync when parent provides a fresher address (e.g., after fetch)
  useEffect(() => {
    const next = addressInfo?.addressCard || DEFAULT_ADDRESS;
    setAddressCard(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressInfo]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editField, setEditField] = useState(null);
  const [currentValue, setCurrentValue] = useState(null);

  const openModal = () => {
    setEditField("Address");
    setCurrentValue(addressCard);
    setIsModalOpen(true);
  };

  const handleSave = async (updated) => {
    try {
      await updateUserAddress(updated);

      // Immediately refetch profile to reflect what the backend saved
      const refreshed = await fetchUserProfile();
      const refreshedCard = refreshed?.addressInfo?.addressCard;
      if (refreshedCard) {
        setAddressCard(refreshedCard);
      } else {
        // Fallback to optimistic local update if backend doesn't echo yet
        setAddressCard({
          ...(addressCard || {}),
          address_line1: updated.address_line1 ?? updated.addressLine1 ?? addressCard?.address_line1,
          address_line2: updated.address_line2 ?? updated.addressLine2 ?? addressCard?.address_line2,
          city: updated.city ?? addressCard?.city,
          state: updated.state ?? addressCard?.state,
          country: updated.country ?? addressCard?.country,
          postal_code: updated.postal_code ?? updated.postalCode ?? addressCard?.postal_code
        });
      }
      alert("Address updated");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to update address";
      alert(msg);
    } finally {
      setIsModalOpen(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <div>
          <h3 className="font-medium text-gray-900 text-base mb-2">
            Address and contact information
          </h3>
          <p className="text-xs text-gray-500 leading-relaxed">
            {DESCRIPTION}
          </p>
        </div>

        <div className="border rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-4">{addressCard.title || "Address"}</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
            <div>
              <p className="text-sm text-gray-500">City</p>
              <p className="text-sm font-medium text-gray-900">{addressCard.city || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">State/Province</p>
              <p className="text-sm font-medium text-gray-900">{addressCard.state || "-"}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
            <div>
              <p className="text-sm text-gray-500">Country</p>
              <p className="text-sm font-medium text-gray-900">{addressCard.country || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Postal Code</p>
              <p className="text-sm font-medium text-gray-900">{addressCard.postal_code || "-"}</p>
            </div>
          </div>

          <hr className="my-2" />

          <div className="mb-2">
            <p className="text-sm text-gray-500">Address</p>
            <p className="text-sm font-medium text-gray-900">
              {(addressCard.address_line1 || "").trim()}
              {addressCard.address_line2 ? `, ${addressCard.address_line2}` : ""}
            </p>
          </div>

          <hr className="my-2" />

          <div className="mb-2">
            <p className="text-sm text-gray-500">Company email address</p>
            <p className="text-sm font-medium text-gray-900">{addressCard.companyEmail}</p>
          </div>

          <button
            className="text-teal-600 text-sm font-medium mt-2"
            onClick={openModal}
          >
            Update
          </button>
        </div>
      </div>

      {isModalOpen && (
        <UpdateModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          field={editField}
          currentValue={currentValue}
        />
      )}
    </div>
  );
}
